import { useState, useEffect } from 'react'
import { IconAlertCircle, IconCheck, IconPlus, IconX } from '@tabler/icons-react'
import { Layout } from 'components/Layout'
import { ArcPageShell } from 'components/ArcPageShell'
import { Button } from 'components/Button'
import { CurrentUserContextProvider, useCurrentUser } from 'context/useCurrentUser'
import { notifications, NotificationProvider } from 'context'
import { useTranslation } from 'react-i18next'
import type { ManagedFaculty } from './types'
import { FacultySearch } from './components/FacultySearch'
import { FacultyGrid } from './components/FacultyGrid'
import { AddFacultyModal } from './components/AddFacultyModal'
import { EditFacultyModal } from './components/EditFacultyModal'
import { RemoveFacultyModal } from './components/RemoveFacultyModal'
import { useManageFaculty } from './hooks/useManageFaculty'

function ManageFacultyView() {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const {
    faculty,
    grades,
    groups,
    loading,
    error,
    totalPages,
    filters,
    setSearch,
    setPage,
    addFaculty,
    editFaculty,
    removeFaculty,
    addGroup,
  } = useManageFaculty()

  useEffect(() => {
    if (error) {
      notifications.show({
        message: t('manage_faculty.load_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    }
  }, [error, t])

  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editingFaculty, setEditingFaculty] = useState<ManagedFaculty | null>(null)
  const [removingFaculty, setRemovingFaculty] = useState<ManagedFaculty | null>(null)

  const handleAddSave = async (rows: Array<{
    firstName: string
    lastName: string
    email: string
    gradeIds: string[]
    groupIds: string[]
  }>) => {
    const result = await addFaculty(rows)
    setAddModalOpen(false)

    if (result.addedTutors.length > 0) {
      notifications.show({
        message: t('manage_faculty.toast_add_success', {
          count: result.addedTutors.length,
        }),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    }

    if (result.failedTutors.length > 0) {
      notifications.show({
        message: t('manage_faculty.toast_add_partial', {
          count: result.failedTutors.length,
        }),
        color: 'yellow',
        icon: <IconAlertCircle size={18} />,
      })
    }
  }

  const handleEditSave = async (data: {
    firstName: string
    lastName: string
    role: string
    gradeIds: string[]
    groupIds: string[]
  }) => {
    if (!editingFaculty) return
    try {
      await editFaculty(editingFaculty.id, data)
      setEditingFaculty(null)
      notifications.show({
        message: t('manage_faculty.toast_edit_success'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    } catch {
      notifications.show({
        message: t('manage_faculty.toast_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    }
  }

  const handleRemoveConfirm = async () => {
    if (!removingFaculty) return
    const targetFaculty = removingFaculty
    const isSelfRemoval = targetFaculty.email === currentUser?.email
    try {
      await removeFaculty(targetFaculty.id)
      setRemovingFaculty(null)
      notifications.show({
        message: t('manage_faculty.toast_remove_success'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
    } catch {
      notifications.show({
        message: isSelfRemoval
          ? t('manage_faculty.toast_remove_self_error')
          : t('manage_faculty.toast_error'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    }
  }

  return (
    <Layout>
      <ArcPageShell
        title={t('manage_faculty.title')}
        subtitle={t('manage_faculty.subtitle')}
        backTo='/teacher/settings'
        backLabel={t('manage_faculty.back_to_settings')}
        actions={
          <Button variant='filled' size='md' leftIcon={<IconPlus size={18} />} onClick={() => setAddModalOpen(true)}>
            {t('manage_faculty.add_faculty')}
          </Button>
        }
      >
        <FacultySearch
          search={filters.search}
          onSearchChange={setSearch}
        />

        <FacultyGrid
          faculty={faculty}
          loading={loading}
          page={filters.page}
          totalPages={totalPages}
          onPageChange={setPage}
          onEdit={setEditingFaculty}
          onRemove={setRemovingFaculty}
          onAddFaculty={() => setAddModalOpen(true)}
        />

        <AddFacultyModal
          opened={addModalOpen}
          grades={grades}
          groups={groups}
          onClose={() => setAddModalOpen(false)}
          onSave={handleAddSave}
          onAddGroup={addGroup}
        />

        <EditFacultyModal
          key={editingFaculty?.id ?? 'none'}
          faculty={editingFaculty}
          grades={grades}
          groups={groups}
          onClose={() => setEditingFaculty(null)}
          onSave={handleEditSave}
          onAddGroup={addGroup}
        />

        <RemoveFacultyModal
          faculty={removingFaculty}
          onClose={() => setRemovingFaculty(null)}
          onConfirm={handleRemoveConfirm}
        />
      </ArcPageShell>
    </Layout>
  )
}

export function ManageFacultyPage() {
  return (
    <CurrentUserContextProvider>
      <NotificationProvider>
        <ManageFacultyView />
      </NotificationProvider>
    </CurrentUserContextProvider>
  )
}
